import { useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { Box, chakra } from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { SWIPE_ACTION_WIDTH, isFullSwipe, settleSwipe, swipeAxis } from './swipe';
import styles from './SwipeToDelete.module.css';

interface SwipeToDeleteProps {
  open: boolean;
  /** Being deleted: the content leaves to the left (see NotificationRow for the collapse). */
  exiting?: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: () => void;
  children: ReactNode;
}

type Gesture = {
  pointerId: number;
  x: number;
  y: number;
  axis: 'x' | 'y' | null;
  dx: number;
  width: number;
  armed: boolean;
};

/**
 * Swipe a row left to reveal a Delete action; keep swiping (or swipe again once it is
 * open) past half the row and releasing deletes it, as in Mail. Touch only: mouse and
 * keyboard users get the row's own trash button, so the action here is hidden from
 * assistive tech.
 */
export function SwipeToDelete({
  open,
  exiting = false,
  onOpenChange,
  onDelete,
  children,
}: SwipeToDeleteProps) {
  const gesture = useRef<Gesture | null>(null);
  // A horizontal drag can end with a click on the row underneath; swallow it so a swipe
  // never navigates. Touch drags often fire no click at all, so the flag only lives until
  // the next pointerdown -- otherwise it would eat the tap on Delete.
  const swallowClick = useRef(false);
  // Where the content sits mid-drag, and whether releasing there would delete.
  const [drag, setDrag] = useState<{ offset: number; armed: boolean } | null>(null);

  const base = open ? -SWIPE_ACTION_WIDTH : 0;
  const offset = drag ? drag.offset : base;

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    swallowClick.current = false;
    if (exiting || e.pointerType !== 'touch') return;
    gesture.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      axis: null,
      dx: 0,
      // jsdom has no layout; a real row always has a width.
      width: e.currentTarget.offsetWidth || window.innerWidth,
      armed: false,
    };
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const dx = e.clientX - g.x;
    if (!g.axis) g.axis = swipeAxis(dx, e.clientY - g.y);
    if (g.axis !== 'x') return;
    g.dx = dx;
    const next = Math.min(0, Math.max(-g.width, base + dx));
    const armed = isFullSwipe(next, g.width);
    if (armed !== g.armed) {
      // Tick as it arms (and disarms), so a release-to-delete never comes as a surprise.
      g.armed = armed;
      void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    }
    setDrag({ offset: next, armed });
  };

  const handlePointerEnd = (e: PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    gesture.current = null;
    setDrag(null);
    if (g.axis !== 'x') return;
    swallowClick.current = true;
    if (cancelled) return;
    const result = settleSwipe(g.dx, open, g.width);
    if (result === 'delete') {
      // Mark it open as it goes, so the exit plays as the red action sweeping across.
      onOpenChange(true);
      onDelete();
      return;
    }
    onOpenChange(result === 'open');
  };

  const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!swallowClick.current) return;
    swallowClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  // The red action fills whatever the content has uncovered; on delete it sweeps across.
  let actionWidth = `${SWIPE_ACTION_WIDTH}px`;
  if (exiting && open) actionWidth = '100%';
  else if (drag) actionWidth = `${Math.max(SWIPE_ACTION_WIDTH, -drag.offset)}px`;
  // Past the delete threshold the label rides the content's edge instead of centring.
  const hugEdge = (drag?.armed ?? false) || (exiting && open);

  return (
    <Box
      position="relative"
      overflow="hidden"
      touchAction="pan-y"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={(e: PointerEvent<HTMLDivElement>) => handlePointerEnd(e, false)}
      onPointerCancel={(e: PointerEvent<HTMLDivElement>) => handlePointerEnd(e, true)}
      onClickCapture={handleClickCapture}
    >
      <chakra.button
        type="button"
        tabIndex={-1}
        aria-hidden
        position="absolute"
        top="0"
        right="0"
        bottom="0"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap="1"
        bg="volyx.error"
        color="white"
        fontSize="xs"
        fontWeight="semibold"
        border="none"
        cursor="pointer"
        // Inline, not style props: these change on every pointer move.
        style={{
          width: actionWidth,
          // Unswiped and deleted from the trash button: stay hidden behind the content.
          opacity: exiting && !open ? 0 : 1,
          alignItems: hugEdge ? 'flex-start' : 'center',
          paddingLeft: hugEdge ? 24 : 0,
          transition: drag ? 'none' : 'width 240ms cubic-bezier(0.4, 0, 1, 1)',
        }}
        onClick={onDelete}
      >
        <Trash2 size={20} aria-hidden />
        Delete
      </chakra.button>
      <Box
        className={styles.content}
        data-exiting={exiting || undefined}
        position="relative"
        bg="white"
        style={
          exiting
            ? {
                transform: 'translateX(-100%)',
                opacity: open ? 1 : 0,
                transition: 'transform 240ms cubic-bezier(0.4, 0, 1, 1), opacity 200ms ease-in',
              }
            : {
                transform: `translateX(${offset}px)`,
                opacity: 1,
                // opacity too: a failed delete fades the row back in rather than popping.
                transition: drag ? 'none' : 'transform 0.2s ease, opacity 0.2s ease',
              }
        }
      >
        {children}
      </Box>
    </Box>
  );
}
